import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { DHLError } from './CustomErrors';

/**
 * Merges two objects together. Also merges headers property together from two objects.
 * Allows for request to also add header properties without overwriting the original object's headers.
 *
 * @param {object} config first object to merge
 * @param {object} options second object to merge
 * @returns {object} object containing both first and second objects. if both have the 'header' property, merge this together
 */
const mergeHeaders = (config, options) => {
  if (config.headers && options.headers) {
    return { ...config, ...options, headers: { ...config.headers, ...options.headers } };
  }
  return { ...config, ...options };
};

/**
 * Creates an axios request with `transactionid` (correlation id) header.
 * Allows for a request to have universal values to be added to axios requests
 * - Currently, adds the correlation id to the headers for axios requests
 *
 * @returns get, post and patch methods for axios with added correlation id
 */
export const axiosRequest = () => {
  const config = {
    headers: {
      transactionid: uuid(),
    },
  };

  return {
    get: (url, options = {}) => {
      return axios.get(url, mergeHeaders(config, options));
    },
    post: (url, data, options = {}) => {
      return axios.post(url, data, mergeHeaders(config, options));
    },
    patch: (url, data, options = {}) => {
      return axios.patch(url, data, mergeHeaders(config, options));
    },
  };
};

/**
 *  Create a function to check whether the response was successful or not
 *  return true response was successful (status in the range 200-299), otherwise false
 *
 * @param {object} response http response
 *  @returns {boolean}
 */
export const isSuccessfulResponse = (response) => {
  // Sometimes response.ok is undefined, in case avoid this happen, regenerate new response and get the ok value
  const options = {
    status: response.status,
    statusText: response.statusText,
  };

  const res = new Response(null, options);
  return res.ok;
};

/**
 *  Create a function to check whether the response was successful or not
 *  When response was successful (status in the range 200-299) and has data, return data, otherwise throw error
 *
 * @param {object} response http response
 *  @returns responses data
 */

export const handleHttpResponse = (response) => {
  if (!response.data) {
    throw new DHLError(`No data in the HTTP response (HTTP status ${response.status})`);
  }

  if (!isSuccessfulResponse(response)) {
    throw new DHLError(`HTTP error ${response.status}, ${response.statusText}`);
  }

  return response.data;
};
